import { FormSection } from '@/types/FormSectionInterface'
import { TestType } from '@/types/RequisitionFormTypes'
import React, { forwardRef, useImperativeHandle, useState } from 'react'

interface ReviewData {
  patientInfo: {
    firstName: string
    lastName: string
    middleName: string
    dateOfBirth: string
    sex: string
    mrn: string
    collectionType: string
  }
  clinicalInfo: {
    clinicalNote: string
    optOutClinicianNotes: boolean
  }
  reasonInfo: {
    clinicalDiagnosis: Array<{
      id: string
      label: string
      description?: string
      category: 'Key' | 'Additional' | 'Excluded'
    }>
    icd10Diagnosis: Array<{
      id: string
      label: string
      description?: string
      category: 'Key' | 'Additional' | 'Excluded'
    }>
  }
  familyInfo: {
    familyHistory: string
    hasSibling: boolean
    hasParent: boolean
    hasGrandparent: boolean
    hasCousin: boolean
  }
  motherInfo: {
    parentFirstName: string
    parentLastName: string
    parentDateOfBirth: string
    parentMRN: string
    parentPhone: string
    parentEmail: string
    parentMedicalHistory: string
    pregnancyHistory: string
    birthComplications: string
    phenotypicTrait: string
    hpoTerms: Array<{ id: string; label: string; description?: string; category?: 'Key' | 'Additional' | 'Excluded' }>
    icd10Terms: Array<{ id: string; label: string; description?: string }>
    collectionType: string
  }
  fatherInfo: {
    parentFirstName: string
    parentLastName: string
    parentDateOfBirth: string
    parentMRN: string
    parentPhone: string
    parentEmail: string
    parentMedicalHistory: string
    pregnancyHistory: string
    birthComplications: string
    phenotypicTrait: string
    hpoTerms: Array<{ id: string; label: string; description?: string; category?: 'Key' | 'Additional' | 'Excluded' }>
    icd10Terms: Array<{ id: string; label: string; description?: string }>
    collectionType: string
  }
  orderInfo: {
    selectedOption: string
  }
  consentInfo: {
    providerName: string
    providerEmail: string
    status: string
    isSigningComplete: boolean
  }
}

interface ReviewProps {
  formData?: Record<string, unknown>
  onConfirm?: () => void
  onEdit?: (section: string) => void
  showConsentSection?: boolean
}

const Review = forwardRef<FormSection, ReviewProps>(({ formData, onEdit, showConsentSection }, ref) => {
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({})
  const [expandedTerms, setExpandedTerms] = useState<Set<string>>(new Set())

  const toggleSectionCollapse = (sectionId: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }

  const getFirstWrappedLine = (text: string, maxLength: number = 80): string => {
    // If text is shorter than maxLength, return the whole text
    if (text.length <= maxLength) {
      return text
    }

    // Find the last space before maxLength to avoid cutting words
    const truncated = text.substring(0, maxLength)
    const lastSpaceIndex = truncated.lastIndexOf(' ')

    if (lastSpaceIndex > maxLength * 0.7) {
      // If we found a space in a reasonable position
      return text.substring(0, lastSpaceIndex) + '...'
    } else {
      return truncated + '...'
    }
  }

  const isMultiLine = (text: string): boolean => {
    return text.length > 80 // Show expand button for longer text
  }

  const toggleExpanded = (termId: string) => {
    setExpandedTerms((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(termId)) {
        newSet.delete(termId)
      } else {
        newSet.add(termId)
      }
      return newSet
    })
  }

  // Helper function to sort terms by category (Key, Additional, Excluded)
  const sortTermsByCategory = <T extends { category?: 'Key' | 'Additional' | 'Excluded'; label: string }>(
    terms: T[]
  ): T[] => {
    const categoryOrder = { Key: 0, Additional: 1, Excluded: 2 }
    return [...terms].sort((a, b) => {
      const categoryA = categoryOrder[a.category || 'Key']
      const categoryB = categoryOrder[b.category || 'Key']
      if (categoryA !== categoryB) {
        return categoryA - categoryB
      }
      // If same category, sort alphabetically by label
      return a.label.localeCompare(b.label)
    })
  }

  // Map formData to ReviewData structure
  const patientData = (formData?.patient as Record<string, unknown>) || {}
  const clinicalData = (formData?.clinical as Record<string, unknown>) || {}
  const reasonData = (formData?.reason as Record<string, unknown>) || {}
  const familyData = (formData?.family as Record<string, unknown>) || {}
  const motherData = (formData?.mother as Record<string, unknown>) || {}
  const fatherData = (formData?.father as Record<string, unknown>) || {}
  const orderData = (formData?.order as Record<string, unknown>) || {}
  const consentData = (formData?.consent as Record<string, unknown>) || {}

  // Helper functions to determine which parent sections should be shown
  const shouldShowParentSections = () => {
    const selectedOption = orderData.selectedOption as string
    return selectedOption !== TestType.rWGS_Patient_Only && selectedOption !== TestType.NONE
  }

  const shouldShowSingleParentSection = () => {
    const selectedOption = orderData.selectedOption as string
    return selectedOption === TestType.rWGS_Duo
  }

  const reviewData: ReviewData = {
    patientInfo: {
      firstName: String(patientData.firstName || 'Not provided'),
      lastName: String(patientData.lastName || 'Not provided'),
      middleName: String(patientData.middleName || 'Not provided'),
      dateOfBirth: String(patientData.dateOfBirth || 'Not provided'),
      sex: String(patientData.sex || 'Not provided'),
      mrn: String(patientData.mrn || 'Not provided'),
      collectionType: String(patientData.collectionType || 'Not provided'),
    },
    clinicalInfo: {
      clinicalNote: String(clinicalData.clinicalNote || 'Not provided'),
      optOutClinicianNotes: Boolean(clinicalData.optOutClinicianNotes || false),
    },
    reasonInfo: {
      clinicalDiagnosis: Array.isArray(reasonData.clinicalDiagnosis)
        ? sortTermsByCategory(
            reasonData.clinicalDiagnosis as Array<{
              id: string
              label: string
              description?: string
              category: 'Key' | 'Additional' | 'Excluded'
            }>
          )
        : [],
      icd10Diagnosis: Array.isArray(reasonData.icd10Diagnosis)
        ? (reasonData.icd10Diagnosis as Array<{
            id: string
            label: string
            description?: string
            category: 'Key' | 'Additional' | 'Excluded'
          }>)
        : typeof reasonData.icd10Diagnosis === 'string' && reasonData.icd10Diagnosis.trim() !== ''
          ? [
              {
                id: 'custom',
                label: reasonData.icd10Diagnosis,
                description: 'Custom ICD-10 diagnosis',
                category: 'Key' as const,
              },
            ]
          : [],
    },
    familyInfo: {
      familyHistory: String(familyData.familyHistory || 'Not provided'),
      hasSibling: Boolean(familyData.hasSibling || false),
      hasParent: Boolean(familyData.hasParent || false),
      hasGrandparent: Boolean(familyData.hasGrandparent || false),
      hasCousin: Boolean(familyData.hasCousin || false),
    },
    motherInfo: {
      parentFirstName: String(motherData.parentFirstName || 'Not provided'),
      parentLastName: String(motherData.parentLastName || 'Not provided'),
      parentDateOfBirth: String(motherData.parentDateOfBirth || 'Not provided'),
      parentMRN: String(motherData.parentMRN || 'Not provided'),
      parentPhone: String(motherData.parentPhone || 'Not provided'),
      parentEmail: String(motherData.parentEmail || 'Not provided'),
      parentMedicalHistory: String(motherData.parentMedicalHistory || 'Not provided'),
      pregnancyHistory: String(motherData.pregnancyHistory || 'Not provided'),
      birthComplications: String(motherData.birthComplications || 'Not provided'),
      phenotypicTrait: String(motherData.phenotypicTrait || 'Not provided'),
      hpoTerms: Array.isArray(motherData.hpoTerms)
        ? sortTermsByCategory(
            motherData.hpoTerms as Array<{
              id: string
              label: string
              description?: string
              category?: 'Key' | 'Additional' | 'Excluded'
            }>
          )
        : [],
      icd10Terms: Array.isArray(motherData.icd10Terms)
        ? (motherData.icd10Terms as Array<{ id: string; label: string; description?: string }>)
        : [],
      collectionType: String(motherData.collectionType || 'Not provided'),
    },
    fatherInfo: {
      parentFirstName: String(fatherData.parentFirstName || 'Not provided'),
      parentLastName: String(fatherData.parentLastName || 'Not provided'),
      parentDateOfBirth: String(fatherData.parentDateOfBirth || 'Not provided'),
      parentMRN: String(fatherData.parentMRN || 'Not provided'),
      parentPhone: String(fatherData.parentPhone || 'Not provided'),
      parentEmail: String(fatherData.parentEmail || 'Not provided'),
      parentMedicalHistory: String(fatherData.parentMedicalHistory || 'Not provided'),
      pregnancyHistory: String(fatherData.pregnancyHistory || 'Not provided'),
      birthComplications: String(fatherData.birthComplications || 'Not provided'),
      phenotypicTrait: String(fatherData.phenotypicTrait || 'Not provided'),
      hpoTerms: Array.isArray(fatherData.hpoTerms)
        ? sortTermsByCategory(
            fatherData.hpoTerms as Array<{
              id: string
              label: string
              description?: string
              category?: 'Key' | 'Additional' | 'Excluded'
            }>
          )
        : [],
      icd10Terms: Array.isArray(fatherData.icd10Terms)
        ? (fatherData.icd10Terms as Array<{ id: string; label: string; description?: string }>)
        : [],
      collectionType: String(fatherData.collectionType || 'Not provided'),
    },
    orderInfo: {
      selectedOption: String(orderData.selectedOption || 'Not provided'),
    },
    consentInfo: {
      providerName: String(consentData.providerName || 'Not provided'),
      providerEmail: String(consentData.providerEmail || 'Not provided'),
      status: String(consentData.status || 'Not provided'),
      isSigningComplete: Boolean(consentData.isSigningComplete),
    },
  }

  const getOrderTypeLabel = (code: string) => {
    const orderTypes: Record<string, string> = {
      'F11-03': 'urWGS Trio (one individual and two biological parents)',
      'F10-01': 'rWGS Patient Only (one individual)',
      'F10-02': 'rWGS Duo (one individual and one biological parent)',
      'F10-03': 'rWGS Trio (one individual and two biological parents)',
    }
    return orderTypes[code] || code
  }

  const getCategoryColors = (category: 'Key' | 'Additional' | 'Excluded') => {
    const colors = {
      Key: 'bg-green-100 text-green-800 border-green-200',
      Additional: 'bg-blue-100 text-blue-800 border-blue-200',
      Excluded: 'bg-red-100 text-red-800 border-red-200',
    }
    return colors[category]
  }

  const renderParentInfo = (parentData: ReviewData['motherInfo'] | ReviewData['fatherInfo']) => {
    return (
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="font-medium">Name:</span> {parentData.parentFirstName} {parentData.parentLastName}
          </div>
          <div>
            <span className="font-medium">Date of Birth:</span> {parentData.parentDateOfBirth}
          </div>
          <div>
            <span className="font-medium">MRN:</span> {parentData.parentMRN}
          </div>
          <div>
            <span className="font-medium">Phone:</span> {parentData.parentPhone}
          </div>
          <div>
            <span className="font-medium">Email:</span> {parentData.parentEmail}
          </div>
          <div>
            <span className="font-medium">Phenotypic Trait:</span> {parentData.phenotypicTrait}
          </div>
          <div>
            <span className="font-medium">Collection Type:</span> {parentData.collectionType}
          </div>
        </div>

        {parentData.hpoTerms.length > 0 && (
          <div>
            <span className="font-medium">HPO Terms:</span>
            <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 mt-1 max-h-64 space-y-3 overflow-y-auto pr-2">
              {parentData.hpoTerms.map((term, index) => (
                <div
                  key={`hpo-${index}`}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`rounded border px-2 py-1 text-xs ${getCategoryColors(term.category || 'Key')}`}>
                        {term.category || 'Key'}
                      </span>
                      <span className="font-mono text-xs text-blue-600">{term.id}</span>
                      <span className="truncate text-sm font-medium text-gray-900">{term.label}</span>
                    </div>
                    {term.description && term.description !== term.label && (
                      <div className="mt-1">
                        <p className="text-xs text-gray-600">
                          {expandedTerms.has(term.id) ? term.description : getFirstWrappedLine(term.description)}
                        </p>
                        {isMultiLine(term.description) && (
                          <button
                            onClick={() => toggleExpanded(term.id)}
                            className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                          >
                            {expandedTerms.has(term.id) ? (
                              <>
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 15l7-7 7 7"
                                  />
                                </svg>
                                Show less
                              </>
                            ) : (
                              <>
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                                Show more
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {parentData.icd10Terms.length > 0 && (
          <div>
            <span className="font-medium">ICD-10 Diagnosis Codes:</span>
            <div className="mt-1 rounded border bg-white p-2 text-gray-700">
              {parentData.icd10Terms.map((term, index) => (
                <div key={`icd10-${index}`} className="flex items-center gap-2">
                  <span className="font-mono text-xs text-green-600">{term.id}</span>
                  <span>{term.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const CollapsibleSection = ({
    title,
    sectionId,
    onEdit,
    children,
  }: {
    title: string
    sectionId: string
    onEdit?: () => void
    children: React.ReactNode
  }) => {
    const isCollapsed = collapsedSections[sectionId] || false

    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => toggleSectionCollapse(sectionId)}
            className="flex items-center space-x-2 text-left hover:text-gray-600"
          >
            <h4 className="font-medium text-gray-900">{title}</h4>
            <svg
              className={`h-4 w-4 transform transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {onEdit && (
            <button onClick={onEdit} className="text-sm text-indigo-600 hover:text-indigo-800">
              <img src="/icons/edit.svg" alt="Edit" className="h-4 w-4" />
            </button>
          )}
        </div>
        {!isCollapsed && <div className="px-4 pb-4">{children}</div>}
      </div>
    )
  }

  // Implement the FormSection interface
  useImperativeHandle(ref, () => ({
    isSectionComplete: () => {
      // Review section is always complete since it's just for review
      return true
    },
    renderSection: () => {
      return (
        <div className="space-y-6">
          <h3 className="section-header border-b border-gray-200 pb-2">Order Review & Summary</h3>

          {/* Order Information Review */}
          <CollapsibleSection title="Order Information" sectionId="order-info" onEdit={() => onEdit?.('order-type')}>
            <div className="text-sm">
              <div>
                <span className="font-medium">Order Type:</span>{' '}
                {getOrderTypeLabel(reviewData.orderInfo.selectedOption)}
              </div>
            </div>
          </CollapsibleSection>

          {/* Patient Information Review */}
          <CollapsibleSection
            title="Patient Information"
            sectionId="patient-info"
            onEdit={() => onEdit?.('patient-information')}
          >
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="font-medium">First Name:</span> {reviewData.patientInfo.firstName}
              </div>
              <div>
                <span className="font-medium">Last Name:</span> {reviewData.patientInfo.lastName}
              </div>
              <div>
                <span className="font-medium">Middle Name:</span> {reviewData.patientInfo.middleName || 'N/A'}
              </div>
              <div>
                <span className="font-medium">Date of Birth:</span> {reviewData.patientInfo.dateOfBirth}
              </div>
              <div>
                <span className="font-medium">Sex:</span> {reviewData.patientInfo.sex}
              </div>
              <div>
                <span className="font-medium">MRN:</span> {reviewData.patientInfo.mrn}
              </div>
              <div>
                <span className="font-medium">Collection Type:</span> {reviewData.patientInfo.collectionType}
              </div>
            </div>
          </CollapsibleSection>

          {/* Clinical Information Review */}
          <CollapsibleSection
            title="Clinical Information"
            sectionId="clinical-info"
            onEdit={() => onEdit?.('clinical-information')}
          >
            <div className="space-y-3 text-sm">
              {reviewData.clinicalInfo.optOutClinicianNotes ? (
                <div>
                  <span className="font-medium">Clinical Notes:</span>
                  <div className="mt-1 rounded border bg-white p-2 text-gray-700">
                    <em>Opted out of entering clinician notes</em>
                  </div>
                </div>
              ) : (
                <div>
                  <span className="font-medium">Clinical Note:</span>
                  <div className="mt-1 whitespace-pre-wrap rounded border bg-white p-2 text-gray-700">
                    {reviewData.clinicalInfo.clinicalNote}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Reason for Testing Review */}
          <CollapsibleSection
            title="Reason for Testing"
            sectionId="reason-testing"
            onEdit={() => onEdit?.('reason-for-testing')}
          >
            <div className="space-y-3 text-sm">
              {/* HPO Terms */}
              {reviewData.reasonInfo.clinicalDiagnosis.length > 0 && (
                <div>
                  <span className="font-medium">HPO Terms:</span>
                  <div className="mt-1 rounded border bg-white p-2 text-gray-700">
                    <div className="space-y-2">
                      {reviewData.reasonInfo.clinicalDiagnosis.map((diagnosis, index) => (
                        <div key={`hpo-${index}`} className="flex items-center gap-2">
                          <span className={`rounded border px-2 py-1 text-xs ${getCategoryColors(diagnosis.category)}`}>
                            {diagnosis.category}
                          </span>
                          <span className="font-mono text-xs text-blue-600">{diagnosis.id}</span>
                          <span>{diagnosis.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ICD-10 Codes */}
              {reviewData.reasonInfo.icd10Diagnosis.length > 0 && (
                <div>
                  <span className="font-medium">ICD-10 Diagnosis Codes:</span>
                  <div className="mt-1 rounded border bg-white p-2 text-gray-700">
                    <div className="space-y-2">
                      {reviewData.reasonInfo.icd10Diagnosis.map((diagnosis, index) => (
                        <div key={`icd10-${index}`} className="flex items-center gap-2">
                          <span className={`rounded border px-2 py-1 text-xs ${getCategoryColors(diagnosis.category)}`}>
                            {diagnosis.category}
                          </span>
                          <span className="font-mono text-xs text-green-600">{diagnosis.id}</span>
                          <span>{diagnosis.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Show message when no terms are selected */}
              {reviewData.reasonInfo.clinicalDiagnosis.length === 0 &&
                reviewData.reasonInfo.icd10Diagnosis.length === 0 && (
                  <div>
                    <span className="font-medium">Clinical Diagnosis:</span>
                    <div className="mt-1 rounded border bg-white p-2 text-gray-700">Not provided</div>
                  </div>
                )}
            </div>
          </CollapsibleSection>

          {/* Family History Review */}
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="font-medium text-gray-900">Family History</h4>
              <button
                onClick={() => onEdit?.('family-history')}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                <img src="/icons/edit.svg" alt="Edit" className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <span className="font-medium">Family Members with Key Phenotypes:</span>
                <div className="mt-1 flex gap-2">
                  {reviewData.familyInfo.hasParent && (
                    <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">Parent</span>
                  )}
                  {reviewData.familyInfo.hasGrandparent && (
                    <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">Grandparent</span>
                  )}
                  {reviewData.familyInfo.hasSibling && (
                    <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">Sibling</span>
                  )}
                  {reviewData.familyInfo.hasCousin && (
                    <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">Cousin</span>
                  )}
                  {!reviewData.familyInfo.hasParent &&
                    !reviewData.familyInfo.hasGrandparent &&
                    !reviewData.familyInfo.hasSibling &&
                    !reviewData.familyInfo.hasCousin && <span className="text-gray-500">None selected</span>}
                </div>
              </div>
              <div>
                <span className="font-medium">Family History:</span>
                <div className="mt-1 whitespace-pre-wrap rounded border bg-white p-2 text-gray-700">
                  {reviewData.familyInfo.familyHistory}
                </div>
              </div>
            </div>
          </div>

          {/* Consent Information Review */}
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="font-medium text-gray-900">Consent Information</h4>
              <button onClick={() => onEdit?.('consent')} className="text-sm text-indigo-600 hover:text-indigo-800">
                <img src="/icons/edit.svg" alt="Edit" className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              {reviewData.consentInfo.isSigningComplete ? (
                <div className="flex items-center gap-2 text-green-700">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="font-medium">DocuSign Attestation Complete</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-700">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="font-medium">DocuSign Attestation Incomplete</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium">Provider Name:</span> {reviewData.consentInfo.providerName}
                </div>
                <div>
                  <span className="font-medium">Provider Email:</span> {reviewData.consentInfo.providerEmail}
                </div>
                <div>
                  <span className="font-medium">Status:</span> {reviewData.consentInfo.status}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    renderSectionInfo: () => {
      return <div></div>
    },
  }))

  return (
    <div className="space-y-6">
      <h3 className="section-header border-b border-gray-200 pb-2">Order Review & Summary</h3>

      {/* Order Information Review */}
      <CollapsibleSection title="Order Information" sectionId="order-info" onEdit={() => onEdit?.('order-type')}>
        <div className="text-sm">
          <div>
            <span className="font-medium">Order Type:</span> {getOrderTypeLabel(reviewData.orderInfo.selectedOption)}
          </div>
        </div>
      </CollapsibleSection>

      {/* Patient Information Review */}
      <CollapsibleSection
        title="Patient Information"
        sectionId="patient-info"
        onEdit={() => onEdit?.('patient-information')}
      >
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="font-medium">First Name:</span> {reviewData.patientInfo.firstName}
          </div>
          <div>
            <span className="font-medium">Last Name:</span> {reviewData.patientInfo.lastName}
          </div>
          <div>
            <span className="font-medium">Middle Name:</span> {reviewData.patientInfo.middleName || 'N/A'}
          </div>
          <div>
            <span className="font-medium">Date of Birth:</span> {reviewData.patientInfo.dateOfBirth}
          </div>
          <div>
            <span className="font-medium">Sex:</span> {reviewData.patientInfo.sex}
          </div>
          <div>
            <span className="font-medium">MRN:</span> {reviewData.patientInfo.mrn}
          </div>
          <div>
            <span className="font-medium">Collection Type:</span> {reviewData.patientInfo.collectionType}
          </div>
        </div>
      </CollapsibleSection>

      {/* Clinical Information Review */}
      <CollapsibleSection
        title="Clinical Information"
        sectionId="clinical-info"
        onEdit={() => onEdit?.('clinical-information')}
      >
        <div className="space-y-3 text-sm">
          {reviewData.clinicalInfo.optOutClinicianNotes ? (
            <div>
              <span className="font-medium">Clinical Notes:</span>
              <div className="mt-1 rounded border bg-white p-2 text-gray-700">
                <em>Opted out of entering clinician notes</em>
              </div>
            </div>
          ) : (
            <div>
              <span className="font-medium">Clinical Note:</span>
              <div className="mt-1 whitespace-pre-wrap rounded border bg-white p-2 text-gray-700">
                {reviewData.clinicalInfo.clinicalNote}
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Reason for Testing Review */}
      <CollapsibleSection
        title="Reason for Testing"
        sectionId="reason-testing"
        onEdit={() => onEdit?.('reason-for-testing')}
      >
        <div className="space-y-3 text-sm">
          {/* HPO Terms */}
          {reviewData.reasonInfo.clinicalDiagnosis.length > 0 && (
            <div>
              <span className="font-medium">HPO Terms:</span>
              <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 mt-1 max-h-64 space-y-3 overflow-y-auto pr-2">
                {reviewData.reasonInfo.clinicalDiagnosis.map((diagnosis, index) => (
                  <div
                    key={`hpo-${index}`}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`rounded border px-2 py-1 text-xs ${getCategoryColors(diagnosis.category)}`}>
                          {diagnosis.category}
                        </span>
                        <span className="font-mono text-xs text-blue-600">{diagnosis.id}</span>
                        <span className="truncate text-sm font-medium text-gray-900">{diagnosis.label}</span>
                      </div>
                      {diagnosis.description && diagnosis.description !== diagnosis.label && (
                        <div className="mt-1">
                          <p className="text-xs text-gray-600">
                            {expandedTerms.has(diagnosis.id)
                              ? diagnosis.description
                              : getFirstWrappedLine(diagnosis.description)}
                          </p>
                          {isMultiLine(diagnosis.description) && (
                            <button
                              onClick={() => toggleExpanded(diagnosis.id)}
                              className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                            >
                              {expandedTerms.has(diagnosis.id) ? (
                                <>
                                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 15l7-7 7 7"
                                    />
                                  </svg>
                                  Show less
                                </>
                              ) : (
                                <>
                                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                  Show more
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ICD-10 Codes */}
          {reviewData.reasonInfo.icd10Diagnosis.length > 0 && (
            <div>
              <span className="font-medium">ICD-10 Diagnosis Codes:</span>
              <div className="mt-1 rounded border bg-white p-2 text-gray-700">
                <div className="space-y-2">
                  {reviewData.reasonInfo.icd10Diagnosis.map((diagnosis, index) => (
                    <div key={`icd10-${index}`} className="flex items-center gap-2">
                      <span className={`rounded border px-2 py-1 text-xs ${getCategoryColors(diagnosis.category)}`}>
                        {diagnosis.category}
                      </span>
                      <span className="font-mono text-xs text-green-600">{diagnosis.id}</span>
                      <span>{diagnosis.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Show message when no terms are selected */}
          {reviewData.reasonInfo.clinicalDiagnosis.length === 0 &&
            reviewData.reasonInfo.icd10Diagnosis.length === 0 && (
              <div>
                <span className="font-medium">Clinical Diagnosis:</span>
                <div className="mt-1 rounded border bg-white p-2 text-gray-700">Not provided</div>
              </div>
            )}
        </div>
      </CollapsibleSection>

      {/* Family History Review */}
      <div className="rounded-lg bg-gray-50 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="font-medium text-gray-900">Family History</h4>
          <button onClick={() => onEdit?.('family-history')} className="text-sm text-indigo-600 hover:text-indigo-800">
            <img src="/icons/edit.svg" alt="Edit" className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 text-sm">
          <div>
            <span className="font-medium">Family Members with Key Phenotypes:</span>
            <div className="mt-1 flex gap-2">
              {reviewData.familyInfo.hasParent && (
                <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">Parent</span>
              )}
              {reviewData.familyInfo.hasGrandparent && (
                <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">Grandparent</span>
              )}
              {reviewData.familyInfo.hasSibling && (
                <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">Sibling</span>
              )}
              {reviewData.familyInfo.hasCousin && (
                <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">Cousin</span>
              )}
              {!reviewData.familyInfo.hasParent &&
                !reviewData.familyInfo.hasGrandparent &&
                !reviewData.familyInfo.hasSibling &&
                !reviewData.familyInfo.hasCousin && <span className="text-gray-500">None selected</span>}
            </div>
          </div>
          <div>
            <span className="font-medium">Family History:</span>
            <div className="mt-1 whitespace-pre-wrap rounded border bg-white p-2 text-gray-700">
              {reviewData.familyInfo.familyHistory}
            </div>
          </div>
        </div>
      </div>

      {shouldShowParentSections() && (
        <>
          {(!shouldShowSingleParentSection() ||
            (shouldShowSingleParentSection() && motherData.parentType === 'mother')) && (
            <>
              {/* Biological Mother Information Review */}
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">Biological Mother Information</h4>
                  <button
                    onClick={() => onEdit?.('biological-parent-information-1')}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    <img src="/icons/edit.svg" alt="Edit" className="h-4 w-4" />
                  </button>
                </div>
                {renderParentInfo(reviewData.motherInfo)}
              </div>
            </>
          )}
          {(!shouldShowSingleParentSection() ||
            (shouldShowSingleParentSection() && fatherData.parentType === 'father')) && (
            <>
              {/* Biological Father Information Review */}
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">Biological Father Information</h4>
                  <button
                    onClick={() => onEdit?.('biological-parent-information-2')}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    <img src="/icons/edit.svg" alt="Edit" className="h-4 w-4" />
                  </button>
                </div>
                {renderParentInfo(reviewData.fatherInfo)}
              </div>
            </>
          )}
        </>
      )}
      {/* Consent Information Review */}
      {showConsentSection && (
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-gray-900">Consent Information</h4>
            <button onClick={() => onEdit?.('consent')} className="text-sm text-indigo-600 hover:text-indigo-800">
              <img src="/icons/edit.svg" alt="Edit" className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-3">
            {reviewData.consentInfo.isSigningComplete ? (
              <div className="flex items-center gap-2 text-green-700">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-medium">DocuSign Attestation Complete</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-red-700">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-medium">DocuSign Attestation Incomplete</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="font-medium">Provider Name:</span> {reviewData.consentInfo.providerName}
              </div>
              <div>
                <span className="font-medium">Provider Email:</span> {reviewData.consentInfo.providerEmail}
              </div>
              <div>
                <span className="font-medium">Status:</span> {reviewData.consentInfo.status}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

export default Review
